import React, {FC, useEffect, useState} from 'react';
import {Tab, TabList, TabPanel, Tabs} from 'react-tabs';
import {useLocation, useParams} from 'react-router';
import {MatchUrlParams, Sport, Team} from 'utils/types';
import {ReactTitle} from 'react-meta-tags';
import {format} from 'date-fns';
import {DFPSlotsProvider} from 'react-dfp';

// components
import PillTabList from 'components/Common/PillTabList';
import HeaderInfo from 'components/Match/HeaderInfo';
import WidgetField from 'components/Match/WidgetField';
import Timeline from 'components/Match/Timeline';
import TimelineCommentary from 'components/Match/TimelineCommentary';
import Videos from 'components/Match/Videos';
import WidgetPreview from 'components/Match/WidgetPreview';
import PanelLineupsStats from 'components/Match/PanelLineupsStats';
import PanelScoresTables from 'components/Match/PanelScoresTables';
import AsyncWidgetScores from 'components/Async/AsyncWidgetScores';
import AsyncWidgetStats from 'components/Async/AsyncWidgetStats';
import WidgetBet from 'components/Match/WidgetBet';
import AsyncWidgetLineups from 'components/Async/AsyncWidgetLineups';
import EceArticleView from 'components/Match/EceArticleView';
import AsyncWidgetTables from 'components/Async/AsyncWidgetTables';
import Loader from 'components/Common/Loader';
import Ad from 'components/Common/Ad';

// hooks
import useMatchInfo from 'hooks/useMatchInfo';
import useMatchLiveData from 'hooks/useMatchLiveData';
import useTimelineData from 'hooks/useTimelineData';
import usePolling from 'hooks/usePolling';
import useMatchScorers from 'hooks/useMatchScorers';
import useTimelineCommentary from 'hooks/useTimelineCommentary';
import useTimelineCommentaryEnabled from 'hooks/useTimelineCommentaryEnabled';
import useBetData from 'hooks/useBetData';
import useTimelineVideos from 'hooks/useTimelineVideos';
import useBreakpointXL from 'hooks/useBreakpointXL';
import useBreakpointLG from 'hooks/useBreakpointLG';

import getConfig from '../../getConfig';

import './styles.scss';

// TODO: fallback if URL invalid
// TODO: skeleton loader
const {pollingRates} = getConfig();

// BIQ: which file should this be put in?
// FIXME
const tabsPerSport = (sport: Sport): string[] => {
  switch (sport) {
    case Sport.SOCCER:
      // return ['PREVIEW', 'LINEUPS', 'STATS', 'SCORES', 'TABLES'];
      return ['PREVIEW', 'LINEUPS', 'STATS'];
    case Sport.VOLLEYBALL:
      // return ['PREVIEW', 'SCORES', 'TABLES'];
      return ['PREVIEW'];
    default:
      // return ['PREVIEW', 'STATS', 'SCORES', 'TABLES'];
      return ['PREVIEW', 'STATS'];
  }
};

/**
 * Displays the detailed information of a single match (including live data).
 * Handles the business logic of fetching (and polling) the data when the 'matchId' changes.
 * Dispatches a clean-up action on unmount that causes all the detail-related states to be
 * reset to their initial values.
 *
 * In order to improve the overall performances of the page, the business logic of fetching/polling
 * the sidebar tabs data has been split into smaller components (Async widget components) that are
 * connected themselves to the redux store via custom hooks.
 */
const MatchPage: FC = () => {
  const {matchId} = useParams<MatchUrlParams>(); // the current match id
  const location = useLocation();

  // 24M HOOKS
  const betData = useBetData();
  const [scorers, {getScorersFromTimeline}] = useMatchScorers();
  const [videos, {getVideosFromTimeline}] = useTimelineVideos();
  const commentary = useTimelineCommentary(matchId);
  const [commentaryEnabled, setCommentaryEnabled] = useTimelineCommentaryEnabled();
  const isBreakpointLG = useBreakpointLG();
  const isBreakpointXL = useBreakpointXL();
  const [selectedTab, setSelectedTab] = useState(isBreakpointXL ? 0 : 1);
  const [selectedSideTab, setSelectedSideTab] = useState(0);

  // BI HOOKS
  const {withPolling, clearAllPollings} = usePolling();
  const [matchInfoState, {getMatchInfoById, cleanup}] = useMatchInfo();
  const [matchLiveDataState, {getMatchLiveDataById}] = useMatchLiveData();
  const [timelineState, {getTimelineCmdByEventAndId}] = useTimelineData();

  // the following line returns a polling version of getTimelineCmdByEventAndId function
  const repeatGetTimelineCmdByEventAndId = withPolling(
    getTimelineCmdByEventAndId,
    pollingRates.timeline,
    true
  );
  const repeatGetMatchLiveDataById = withPolling(getMatchLiveDataById, pollingRates.timeline, true);

  const matchInfo = matchInfoState.data;
  const liveData = matchLiveDataState.data;
  const {lastCmdId, updates} = timelineState.data;

  /**
   * When the matchId changes, fetches the match basic information and relative live data.
   */
  useEffect(() => {
    getMatchInfoById(matchId);
    repeatGetMatchLiveDataById(matchId);

    // cleans up the matchDetail state when the component un-mounts
    return () => {
      cleanup();
      repeatGetMatchLiveDataById.cancel();
    };
  }, [matchId, getMatchInfoById, repeatGetMatchLiveDataById, cleanup]);

  /**
   * When the matchId changes or the lastCmdId changes, repeat fetching the commands
   */
  useEffect(() => {
    repeatGetTimelineCmdByEventAndId(matchId, lastCmdId);

    return repeatGetTimelineCmdByEventAndId.cancel;
  }, [matchId, lastCmdId, repeatGetTimelineCmdByEventAndId]);

  /**
   * When the timeline changes, calculate other visualizations of its data
   */
  useEffect(() => {
    getScorersFromTimeline(timelineState);
    getVideosFromTimeline(timelineState);
  }, [timelineState, getScorersFromTimeline, getVideosFromTimeline]);

  /**
   * When resizing (e.g. portrait / landscape) and tab layouts change, keep live match tab visible.
   */
  useEffect(() => {
    if (isBreakpointLG) {
      setSelectedTab(0);
      setSelectedSideTab(0);
    } else {
      setSelectedTab(0);
    }
  }, [isBreakpointLG]);

  /**
   * The when component un-mounts, clear all the polling functions
   */
  useEffect(() => clearAllPollings, [clearAllPollings]);

  // FIXME: the Loading component should be bound to the `loading` flags,
  // then if no matchInfo is found it just means the provided id is invalid -> the page is a 404
  if (!(matchInfo && liveData)) return <Loader fullpage />;

  const {
    srId,
    date,
    sport,
    competition,
    teams,
    teamRankings,
    probabilities,
    formaMatches,
    formaHome,
    formaAway,
    eceArticleContent,
  } = matchInfo;

  return (
    <DFPSlotsProvider
      dfpNetworkId="4834629"
      targetingArguments={{path: `${process.env.PUBLIC_URL}${location.pathname}`}}
      lazyLoad={{fetchMarginPercent: 500, renderMarginPercent: 10, mobileScaling: 2.0}}
      collapseEmptyDivs
    >
      <div className="MatchPage">
        <ReactTitle
          title={`${teams.home.name} - ${teams.away.name} Live - ${format(
            date,
            'dd-MM-yyyy'
          )} | SPORT24`}
        />
        <header className="MatchPage__header">
          <HeaderInfo
            matchInfo={matchInfo}
            scorers={scorers}
            liveData={liveData}
            betData={betData}
          />
        </header>
        <main
          className={
            `MatchPage__main ${
              isBreakpointLG && !(videos && videos.length) ? 'MatchPage__main--no-tabs' : ''
            }` /* FIXME better tabs visibility check */
          }
        >
          {isBreakpointXL && (
            <section className="MatchPage__left">
              <WidgetBet betData={betData} />
              <Ad
                adUnit="sport24.gr/Matchcenter_ros_300x250A"
                scope="matchpage"
                sizes={[
                  [300, 600],
                  [300, 250],
                ]}
                sizeMapping={[
                  {
                    viewport: [1024, 0],
                    sizes: [
                      [300, 600],
                      [300, 250],
                    ],
                  },
                ]}
                sticky
                autoRefresh
              />
            </section>
          )}
          <section className="MatchPage__center">
            <Tabs
              selectedIndex={selectedTab}
              onSelect={(index, prevIndex) => index !== prevIndex && setSelectedTab(index)}
            >
              <TabList
                className={
                  `MatchPage__tabs ${
                    isBreakpointLG && !(videos && videos.length) ? 'MatchPage__tabs--hidden' : ''
                  }` /* FIXME for dynamic check */
                }
              >
                {/* 
              eceArticleContent && (
                <Tab className="MatchPage__tab" selectedClassName="MatchPage__tab--selected">
                  POST GAME
                </Tab>
              )
              */}
                <Tab className="MatchPage__tab" selectedClassName="MatchPage__tab--selected">
                  LIVE MATCH
                </Tab>
                {!isBreakpointLG && (
                  <Tab className="MatchPage__tab" selectedClassName="MatchPage__tab--selected">
                    PREVIEW
                  </Tab>
                )}
                {!isBreakpointLG && (
                  <Tab className="MatchPage__tab" selectedClassName="MatchPage__tab--selected">
                    {sport === Sport.SOCCER ? (
                      <>
                        LINEUPS <br />& STATS
                      </>
                    ) : (
                      <>STATS</>
                    )}
                  </Tab>
                )}
                {/* 
              !isBreakpointLG && (
                <Tab className="MatchPage__tab" selectedClassName="MatchPage__tab--selected">
                  SCORES <br />& TABLES
                </Tab>
              )
              */}
                {videos && !!videos.length && (
                  <Tab className="MatchPage__tab" selectedClassName="MatchPage__tab--selected">
                    VIDEOS
                  </Tab>
                )}
              </TabList>
              {/*
            eceArticleContent && (
              <TabPanel
                className="MatchPage__panel MatchPage__panel--post-game"
                selectedClassName="MatchPage__panel--selected"
              >
                <EceArticleView article={eceArticleContent} />
              </TabPanel>
            )
            */}
              <TabPanel
                className="MatchPage__panel MatchPage__panel--live"
                selectedClassName="MatchPage__panel--selected"
                forceRender
              >
                {!isBreakpointLG && <WidgetField srId={srId} />}
                <Ad
                  adUnit="sport24.gr/Matchcenter_ros_620x50"
                  scope="matchpage"
                  sizes={[
                    [468, 60],
                    [320, 50],
                  ]}
                  sizeMapping={[
                    {
                      viewport: [1480, 0],
                      sizes: [
                        [468, 60],
                        [320, 50],
                      ],
                    },
                    {viewport: [0, 0], sizes: [[320, 50]]},
                  ]}
                />
                {!isBreakpointLG && (
                  <TimelineCommentary
                    commentary={commentary}
                    commentaryEnabled={commentaryEnabled}
                    setCommentaryEnabled={setCommentaryEnabled}
                  />
                )}
                <Timeline
                  updates={updates}
                  sport={sport}
                  teams={teams}
                  commentaryEnabled={commentaryEnabled}
                />
              </TabPanel>

              {
                /* MOBILE - PREVIEW */
                !isBreakpointLG && (
                  <TabPanel
                    className="MatchPage__panel MatchPage__panel--preview"
                    selectedClassName="MatchPage__panel--selected"
                  >
                    <WidgetPreview
                      sport={sport}
                      teams={teams}
                      teamRankings={teamRankings}
                      probabilities={probabilities}
                      formaMatches={formaMatches}
                      formaHome={formaHome}
                      formaAway={formaAway}
                    />
                  </TabPanel>
                )
              }
              {
                /* MOBILE - LINEUPS & STATS */
                !isBreakpointLG && (
                  <TabPanel
                    className="MatchPage__panel MatchPage__panel--lineups-stats"
                    selectedClassName="MatchPage__panel--selected"
                  >
                    <PanelLineupsStats
                      sport={sport}
                      teams={teams}
                      matchId={matchId}
                      updates={updates}
                    />
                  </TabPanel>
                )
              }
              {/* MOBILE - SCORES & TABLES */
              /*
              !isBreakpointLG && (
                <TabPanel
                  className="MatchPage__panel MatchPage__panel--scores-tables"
                  selectedClassName="MatchPage__panel--selected"
                >
                  <PanelScoresTables
                    seasonId={matchInfo?.seasonId}
                    groupId={matchInfo?.groupId}
                    competition={competition}
                  />
                </TabPanel>
              )
              */}
              {
                /* VIDEOS */
                videos && !!videos.length && (
                  <TabPanel
                    className="MatchPage__panel MatchPage__panel--videos"
                    selectedClassName="MatchPage__panel--selected"
                    forceRender
                  >
                    <Videos videos={videos} />
                  </TabPanel>
                )
              }
            </Tabs>
          </section>
          {
            /* DESKTOP - SIDEBAR TABS */
            isBreakpointLG && (
              <section className="MatchPage__right">
                {isBreakpointLG && <WidgetField srId={srId} />}
                {isBreakpointLG && (
                  <TimelineCommentary
                    commentary={commentary}
                    commentaryEnabled={commentaryEnabled}
                    setCommentaryEnabled={setCommentaryEnabled}
                  />
                )}
                <Tabs
                  className="SideTabs"
                  selectedIndex={selectedSideTab}
                  onSelect={(index, prevIndex) => index !== prevIndex && setSelectedSideTab(index)}
                >
                  {PillTabList({
                    tabs: tabsPerSport(sport),
                  })}
                  <TabPanel>
                    <WidgetPreview
                      sport={sport}
                      teams={teams}
                      teamRankings={teamRankings}
                      probabilities={probabilities}
                      formaMatches={formaMatches}
                      formaHome={formaHome}
                      formaAway={formaAway}
                    />
                  </TabPanel>
                  {sport === Sport.SOCCER && (
                    <TabPanel>
                      <AsyncWidgetLineups
                        matchId={matchId}
                        updates={updates}
                        team={Team.UNDEFINED}
                      />
                    </TabPanel>
                  )}
                  {sport !== Sport.VOLLEYBALL && (
                    <TabPanel>
                      <AsyncWidgetStats matchId={matchId} teams={teams} />
                    </TabPanel>
                  )}
                  {/*
                  <TabPanel>
                    <AsyncWidgetScores
                      seasonId={matchInfo?.seasonId}
                    />
                  </TabPanel>
                  <TabPanel>
                    <AsyncWidgetTables
                      seasonId={matchInfo?.seasonId}
                      groupId={matchInfo?.groupId}
                      competition={competition}
                    />
                  </TabPanel>
                */}
                </Tabs>
                <Ad
                  adUnit="sport24.gr/Matchcenter_ros_300x250B"
                  scope="matchpage"
                  sizes={[
                    [300, 600],
                    [300, 250],
                  ]}
                  sizeMapping={[
                    {
                      viewport: [1024, 0],
                      sizes: [
                        [300, 600],
                        [300, 250],
                      ],
                    },
                  ]}
                  sticky
                  autoRefresh
                />
              </section>
            )
          }
        </main>
        {!isBreakpointLG && (
          // FIXME: umnount his and destroy slot gracefully OR mount and setup
          <Ad
            adUnit="sport24.gr/Matchcenter_ros_300x50stuck"
            scope="matchpage"
            sizes={[[320, 50]]}
            stuck
            autoRefresh
          />
        )}
      </div>
    </DFPSlotsProvider>
  );
};

export default MatchPage;
